import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Stack } from '@mui/material';
import { useAuth } from './useAuth';
import { ErrorState, FullPageLoader } from '@/components/States';
import { AppFooter } from '@/components/AppFooter';

/**
 * Where GitLab's redirect lands, carrying the one-time handoff ticket.
 *
 * @remarks
 * The ticket is exchanged for a session immediately and the URL is replaced, so it does not
 * sit in the address bar or in history. It is single-use and expires in under a minute
 * anyway, but leaving a credential-shaped value on screen invites someone to copy it.
 */
export function AuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { completeSignIn } = useAuth();
  const [error, setError] = useState<unknown>(null);

  // React 18 mounts effects twice in development StrictMode. The ticket is single-use, so
  // the second exchange would fail and show an error after a sign-in that actually worked.
  const exchanged = useRef(false);

  useEffect(() => {
    const ticket = searchParams.get('handoff');

    if (!ticket) {
      setError(new Error('missing handoff'));
      return;
    }

    if (exchanged.current) {
      return;
    }

    exchanged.current = true;

    void (async () => {
      try {
        await completeSignIn(ticket);
        navigate('/', { replace: true });
      } catch (exchangeError) {
        setError(exchangeError);
      }
    })();
  }, [searchParams, completeSignIn, navigate]);

  return (
    <Box sx={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column' }}>
      <Stack flex={1} alignItems="center" justifyContent="center" sx={{ p: 2, width: '100%' }}>
        {error ? (
          <Box sx={{ maxWidth: 480, width: '100%' }}>
            <ErrorState error={error} onRetry={() => navigate('/sign-in', { replace: true })} />
          </Box>
        ) : (
          <FullPageLoader label="در حال تکمیل ورود…" />
        )}
      </Stack>

      <AppFooter />
    </Box>
  );
}
